import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Pagination,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Collections as GalleryIcon,
  SearchOff as EmptyIcon,
} from '@mui/icons-material';
import GalleryCard from './GalleryCard';
import GalleryFilterSidebar from './GalleryFilterSidebar';
import useGalleryStore from '../../../store/galleryStore';
import { fetchGalleryItems, GalleryItem } from '../api/galleryApi';
import useAuth from '../../../features/auth/hooks/useAuth';
import { brand } from '../../../config/brand';

const GalleryBrowsePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useAuth();

  const { filters, setFilters } = useGalleryStore();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = {
          page: filters.page,
          limit: 12,
          sort: filters.sort,
        };
        if (filters.category) params.category = filters.category;
        if (filters.tag) params.tag = filters.tag;
        if (filters.search) params.search = filters.search;

        const res = await fetchGalleryItems(params);
        setItems(res.items);
        setTotalPages(res.totalPages);
        setTotal(res.total);
      } catch (err) {
        console.error('Failed to load gallery:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters]);

  return (
    <Box>
      {/* Hero Banner */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, md: 10 },
          textAlign: 'center',
          overflow: 'hidden',
          color: 'white',
          background: brand.gradients.hero,
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: `rgba(${brand.colorsRgb.fire}, 0.15)`,
            filter: 'blur(80px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `rgba(${brand.colorsRgb.fire}, 0.1)`,
            filter: 'blur(60px)',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: `rgba(${brand.colorsRgb.fire}, 0.15)`,
              border: `1px solid rgba(${brand.colorsRgb.fire}, 0.3)`,
              borderRadius: 5,
              px: 2,
              py: 0.5,
              mb: 2,
            }}
          >
            <GalleryIcon sx={{ fontSize: 18, color: brand.colors.fire }} />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: brand.colors.fire, letterSpacing: 1, textTransform: 'uppercase' }}
            >
              Community Showcase
            </Typography>
          </Box>

          <Typography
            variant="h2"
            fontWeight={800}
            sx={{
              color: 'white',
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            Community App Gallery
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            Discover amazing applications built with {brand.productName}.
            Get inspired, vote for your favorites, and share your own creations.
          </Typography>

          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to={isAuthenticated ? '/gallery/submit' : '/login'}
            startIcon={<AddIcon />}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              background: brand.gradients.button,
              color: brand.buttonTextColor,
              boxShadow: `0 8px 32px rgba(${brand.colorsRgb.fire}, 0.35)`,
              '&:hover': {
                background: brand.gradients.buttonHover,
                boxShadow: `0 12px 40px rgba(${brand.colorsRgb.fire}, 0.45)`,
              },
            }}
          >
            Submit Your App
          </Button>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Grid container spacing={3}>
          {/* Filter sidebar */}
          <Grid item xs={12} md={3} lg={2.5}>
            <GalleryFilterSidebar />
          </Grid>

          {/* Gallery grid */}
          <Grid item xs={12} md={9} lg={9.5}>
            {/* Result count */}
            {!loading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {total} {total === 1 ? 'project' : 'projects'} found
              </Typography>
            )}

            {loading ? (
              <Grid container spacing={3}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Grid item xs={12} sm={6} lg={4} xl={3} key={i}>
                    <Skeleton
                      variant="rounded"
                      height={340}
                      sx={{ borderRadius: 3 }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : items.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 10,
                }}
              >
                <EmptyIcon
                  sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }}
                />
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  No projects found
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Try adjusting your filters or be the first to submit
                  something!
                </Typography>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={isAuthenticated ? '/gallery/submit' : '/login'}
                  startIcon={<AddIcon />}
                  sx={{
                    background: brand.gradients.button,
                    color: brand.buttonTextColor,
                    '&:hover': {
                      background: brand.gradients.buttonHover,
                    },
                  }}
                >
                  Submit Your App
                </Button>
              </Box>
            ) : (
              <>
                <Grid container spacing={3}>
                  {items.map((item) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={4}
                      xl={3}
                      key={item._id}
                    >
                      <GalleryCard item={item} />
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 5,
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={filters.page}
                      onChange={(_, page) => setFilters({ page })}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        '& .Mui-selected': {
                          bgcolor: `${brand.colors.fire} !important`,
                          color: 'white',
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GalleryBrowsePage;
